/* Keep the photograph underneath. Reveal a clip only after a decoded frame. */
(() => {
  const FADE_MS = 1000;
  const PRELOAD_SECONDS = 3;
  class SkyCover extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.videos = [...this.querySelectorAll('video')];
      this.button = this.closest('.yns-hero').querySelector('[data-yns-cover-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.failed = new Set();
      this.active = null;
      this.pending = null;
      this.generation = 0;
      this.inView = false;
      this.userPaused = false;
      this.videos.forEach((video, index) => {
        video.muted = true;
        video.addEventListener('playing', () => {
          clearTimeout(video.stallTimer);
          if (this.pending !== index) return;
          const generation = this.generation;
          const reveal = () => {
            if (generation === this.generation && this.pending === index && this.allowed()) this.reveal(index);
          };
          if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(reveal);
          else requestAnimationFrame(reveal);
        }, options);
        video.addEventListener('timeupdate', () => {
          if (video.currentTime > (video.lastProgressTime ?? -1)) clearTimeout(video.stallTimer);
          video.lastProgressTime = video.currentTime;
          if (index !== this.active || this.pending !== null || this.fading || !this.allowed()) return;
          if (Number.isFinite(video.duration) && video.duration - video.currentTime <= PRELOAD_SECONDS) {
            const next = this.next(index);
            if (next !== null && next !== index) {
              this.load(next);
              if (video.duration - video.currentTime <= FADE_MS / 1000) this.start(next);
            }
          }
        }, options);
        video.addEventListener('ended', () => {
          if (index === this.active && this.pending === null && this.allowed()) this.start(this.next(index));
        }, options);
        video.addEventListener('error', () => this.fail(index), options);
        video.addEventListener('waiting', () => {
          clearTimeout(video.stallTimer);
          video.lastProgressTime = video.currentTime;
          if (index === this.active && this.allowed()) video.stallTimer = setTimeout(() => this.fail(index), 4000);
        }, options);
      });
      this.button.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.sync();
        this.button.setAttribute('aria-pressed', String(this.userPaused));
        this.button.textContent = this.button.dataset[this.userPaused ? 'resume' : 'pause'];
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.sync();
      });
      this.observer.observe(this);
    }
    allowed() {
      const connection = navigator.connection;
      const slow = connection?.saveData || /(^|-)[23]g$/.test(connection?.effectiveType || '');
      return this.isConnected && this.inView && !document.hidden && !this.userPaused &&
        !this.motion.matches && !slow;
    }
    next(after = -1) {
      for (let step = 1; step <= this.videos.length; step++) {
        const index = (after + step) % this.videos.length;
        if (!this.failed.has(index)) return index;
      }
      return null;
    }
    load(index) {
      const video = this.videos[index];
      if (!video.getAttribute('src')) {
        video.src = video.dataset.src;
        video.preload = 'auto';
        video.load();
      }
      return video;
    }
    start(index, resume = false) {
      if (index === null || !this.allowed()) return;
      this.pending = index;
      const generation = ++this.generation;
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.fail(index), 8000);
      const video = this.load(index);
      if (!resume) video.currentTime = 0;
      video.play().catch(() => {
        if (generation === this.generation) this.fail(index);
      });
    }
    reveal(index) {
      clearTimeout(this.loadTimer);
      const previous = this.active;
      this.pending = null;
      this.active = index;
      this.videos.forEach((video, i) => { video.style.zIndex = i === index ? '2' : '1'; });
      this.videos[index].classList.add('is-visible');
      this.button.hidden = false;
      this.fading = true;
      clearTimeout(this.fadeTimer);
      this.fadeTimer = setTimeout(() => {
        this.fading = false;
        if (previous !== null && previous !== this.active) {
          this.videos[previous].classList.remove('is-visible');
          this.videos[previous].pause();
        }
      }, FADE_MS);
    }
    fail(index) {
      this.failed.add(index);
      const video = this.videos[index];
      clearTimeout(video.stallTimer);
      video.classList.remove('is-visible');
      video.pause();
      if (this.pending === index) {
        ++this.generation;
        this.pending = null;
        clearTimeout(this.loadTimer);
      }
      if (this.active === index) this.active = null;
      if (this.next() === null) this.button.hidden = true;
      if (this.pending === null && this.allowed() && (this.active === null || this.videos[this.active].ended)) {
        this.start(this.next(index));
      }
    }
    sync() {
      if (this.allowed()) {
        if (this.pending === null && (this.active === null || this.videos[this.active].paused)) {
          this.start(this.active ?? this.next(), this.active !== null);
        }
        return;
      }
      ++this.generation;
      this.pending = null;
      clearTimeout(this.loadTimer);
      clearTimeout(this.fadeTimer);
      this.fading = false;
      this.videos.forEach((video, i) => {
        clearTimeout(video.stallTimer);
        video.pause();
        if (this.userPaused || i !== this.active || this.motion.matches || navigator.connection?.saveData) video.classList.remove('is-visible');
        // Cancel speculative downloads when playback is suspended. Keep the
        // active source buffered so returning to the cover can resume in place.
        if (i !== this.active && video.getAttribute('src')) {
          video.removeAttribute('src');
          video.preload = 'none';
          video.load();
        }
      });
      if (this.motion.matches || navigator.connection?.saveData) {
        this.active = null;
        this.button.hidden = true;
      }
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.inView = false;
      this.sync();
      this.observer.disconnect();
      this.abort.abort();
      this.abort = null;
    }
  }
  if (!customElements.get('yns-cover')) customElements.define('yns-cover', SkyCover);
})();
